import { TrackingEventName, track } from '@heidi/analytics/events';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { retentionDiscountOfferModalOpenAtom } from '../billingAtoms';
import { useDowngradeFlowButtonAnalyticsProps } from '../hooks/useDowngradeFlowButtonAnalyticsProps';
import { useUpdateExistingSubscription } from '../hooks/useUpdateExistingSubscription';

import { cn } from '@heidi/utils';

import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { CancellationOfferType, CancellationReason } from '@heidi/commerce/contracts';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { DialogDescription, DialogTitle } from '@heidi/design-system/dialog';
import {
  TypographyP,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { AvailableDiscounts } from '@heidi/schema/ml-scribe';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { cancelFlowContextAtom } from '../../../downgrade/cancel-flow-context-atom';

import { useMutateApplySubscriptionDiscount } from '../../../../data/mutation/useMutateApplySubscriptionDiscount';
import { getCommerceBillingUiPorts } from '../billing-ui-ports';
import { useCurrentPlanDisplay } from '../hooks/useCurrentPlanDisplay';

export function RetentionDiscountOfferModal() {
  const intl = useIntl();
  const { planName, currentPaidTier } = useCurrentPlanDisplay();
  const { currentTierFeatures } = getCommerceBillingUiPorts().useFeatureLists({
    currentPaidTier,
  });
  const [open, setOpen] = useAtom(retentionDiscountOfferModalOpenAtom);
  const cancelFlowContext = useAtomValue(cancelFlowContextAtom);
  const setCancelFlowContext = useSetAtom(cancelFlowContextAtom);
  const downgradeFlowButtonAnalyticsProps = useDowngradeFlowButtonAnalyticsProps();
  const hasTrackedShownRef = useRef(false);

  const reason = cancelFlowContext?.selectedReason ?? CancellationReason.other;
  const offerAnalyticsBase = useMemo(
    () => ({
      offer_type: CancellationOfferType.discount,
      reason,
    }),
    [reason]
  );

  useEffect(() => {
    if (!open) {
      hasTrackedShownRef.current = false;
      return;
    }
    if (hasTrackedShownRef.current) return;
    hasTrackedShownRef.current = true;

    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: { screen_name: ScreenName.RetentionDiscountOfferModal },
    });
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.CancelOfferShown,
      props: offerAnalyticsBase,
    });
  }, [open, offerAnalyticsBase]);

  const { redirectToBillingPortalCancel } = useUpdateExistingSubscription();
  const closeOffer = useCallback(() => {
    setOpen(false);
    setCancelFlowContext(null);
    hasTrackedShownRef.current = false;
  }, [setCancelFlowContext, setOpen]);
  const onDiscountApplied = useCallback(() => closeOffer(), [closeOffer]);
  const { mutate: applyRetentionDiscount, isPending: isApplyingDiscount } =
    useMutateApplySubscriptionDiscount({ onDiscountApplied });

  const acceptOfferButtonLabel = intl.formatMessage({
    defaultMessage: 'Get 30% off for 3 months',
    id: 'XHsr/G4kAZ',
  });
  const declineOfferButtonLabel = intl.formatMessage({
    defaultMessage: 'No thanks, cancel anyway',
    id: 'JgcvDSl/t+',
  });

  const onAcceptOffer = useCallback(() => {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.CancelOfferAccepted,
      props: offerAnalyticsBase,
    });
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.RetentionDiscountOfferModal,
        action: ButtonAction.SettingsBillingAcceptDiscountOffer,
        label: acceptOfferButtonLabel,
        ...downgradeFlowButtonAnalyticsProps,
      },
    });
    applyRetentionDiscount({ discount: AvailableDiscounts.downgrade_retention });
  }, [
    acceptOfferButtonLabel,
    applyRetentionDiscount,
    downgradeFlowButtonAnalyticsProps,
    offerAnalyticsBase,
  ]);

  const onDeclineOffer = useCallback(() => {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.CancelOfferDeclined,
      props: offerAnalyticsBase,
    });
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.RetentionDiscountOfferModal,
        action: ButtonAction.SettingsBillingCancelPlanConfirm,
        label: declineOfferButtonLabel,
        ...downgradeFlowButtonAnalyticsProps,
      },
    });
    closeOffer();
    void redirectToBillingPortalCancel();
  }, [
    closeOffer,
    declineOfferButtonLabel,
    downgradeFlowButtonAnalyticsProps,
    offerAnalyticsBase,
    redirectToBillingPortalCancel,
  ]);

  return (
    <DialogDrawer open={open} onOpenChange={setOpen}>
      <DialogDrawerContent
        className={cn(
          'flex max-h-[min(90dvh,640px)] w-[calc(100vw-2rem)] max-w-[470px] gap-0 overflow-hidden p-0',
          'border border-border bg-surface',
          'shadow-[0px_14px_28px_10px_rgba(120,90,60,0.08),0px_36px_80px_40px_rgba(120,90,60,0.12)]',
          'rounded-2xl'
        )}
      >
        <div className="flex min-h-0 w-full flex-col gap-6 overflow-y-auto p-8">
          <div className="flex justify-center">
            <Badge color="green" size="sm">
              {intl.formatMessage({
                defaultMessage: 'Get 30% off',
                id: '2YYv0bC3Eh',
              })}
            </Badge>
          </div>

          <div className="flex flex-col gap-2 text-center">
            <DialogTitle
              className={cn(
                typographyVariant({ variant: 'h2' }),
                'text-balance text-text-primary'
              )}
            >
              {intl.formatMessage(
                {
                  defaultMessage: 'Get 30% off {planName} for 3 months',
                  id: '02rXEVZO5z',
                },
                { planName }
              )}
            </DialogTitle>
            <DialogDescription asChild>
              <TypographyP2 className="text-pretty text-text-secondary">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'An offer just for you. Get 30% off {planName} for 3 months.',
                    id: 'oYTX0kMxoi',
                  },
                  { planName }
                )}
              </TypographyP2>
            </DialogDescription>
          </div>

          <div className="flex flex-col gap-3">
            <TypographyP className="text-pretty text-text-primary">
              {intl.formatMessage({
                defaultMessage: 'Keep using the features that save you hours:',
                id: 'C5ibGFmgQF',
              })}
            </TypographyP>
            <ul className="flex flex-col gap-4">
              {currentTierFeatures.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <li key={`${index}-${feature.description}`} className="flex gap-3">
                    <FeatureIcon
                      aria-hidden
                      className="mt-0.5 size-5 shrink-0 text-text-primary"
                      strokeWidth={1.75}
                    />
                    <TypographyP className="text-pretty text-text-primary">
                      {feature.description}
                    </TypographyP>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-auto flex flex-col gap-2 pt-2">
            <Button
              type="button"
              variant="default"
              size="lg"
              className="w-full"
              disabled={isApplyingDiscount}
              onClick={onAcceptOffer}
            >
              {acceptOfferButtonLabel}
            </Button>
            <Button
              type="button"
              variant="destructive_soft"
              size="lg"
              className="w-full"
              onClick={onDeclineOffer}
            >
              {declineOfferButtonLabel}
            </Button>
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
